import clsx from 'clsx';
import {
  type CSSProperties,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';

import type { ResizableLayoutSize } from '../ResizableLayout/types';
import type { ToolbarItem } from '../Toolbar/types';
import type { AppLayoutProps } from './types';

import { MOBILE_MEDIA_QUERY } from '../../constants/breakpoints';
import { AppNavBreadcrumbsProvider } from '../../context/AppNavBreadcrumbs/AppNavBreadcrumbs';
import { resolveTopLayerRoot } from '../../utils/resolveTopLayerRoot';
import { Layout } from '../Layout/Layout';
import { ResizableLayout } from '../ResizableLayout/ResizableLayout';
import { Toolbar } from '../Toolbar/Toolbar';
import { useElementSize, useMediaQuery } from './hooks';
import { BottomNav } from './subcomponents/BottomNav';
import { BottomSheet } from './subcomponents/BottomSheet';
import { NavDrawer } from './subcomponents/NavDrawer';
import {
  END_CONTENT_MIN_WIDTH,
  getMaxEndContentWidth,
  resolveEndContentWidth,
} from './utils';
import './AppLayout.css';

export const AppLayout = memo(function AppLayout({
  children,
  breadcrumbs,
  dock,
  framed = true,
  header,
  layout = 'bounded',
  mainId,
  rowRef,
  secondaryHeader,
  sideNav,
  sideNavOpen = false,
  topNav,
  onHeaderHeightChange,
  onSideNavClose,
}: AppLayoutProps) {
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
  const [layoutEl, setLayoutEl] = useState<HTMLElement | null>(null);
  const [setTopNavEl, topNavSize] = useElementSize();
  const [setHeaderEl, headerSize] = useElementSize();
  const [setRowEl, rowSize] = useElementSize();
  const [setRailEl, railSize] = useElementSize();
  const overlayRoot = resolveTopLayerRoot(layoutEl);

  const handleRowRef = useCallback(
    (element: HTMLElement | null) => {
      setRowEl(element);
      if (typeof rowRef === 'function') {
        rowRef(element);
      } else if (rowRef) {
        rowRef.current = element;
      }
    },
    [rowRef, setRowEl],
  );

  const headerHeight = header ? (headerSize?.height ?? 0) : 0;
  const topNavHeight = topNav ? (topNavSize?.height ?? 0) : 0;

  useEffect(() => {
    onHeaderHeightChange?.(headerHeight);
  }, [headerHeight, onHeaderHeightChange]);

  const items = dock?.items ?? [];
  const activeItem = items.find((item) => item.key === dock?.activeKey);
  const hasDock = items.length > 0;
  const hasDesktopDock = hasDock && !isMobile;
  const hasMobileDock = hasDock && isMobile;
  const railItems = items.filter((item) => item.icon);

  const maxEndContentWidth = getMaxEndContentWidth(
    rowSize?.width,
    hasDesktopDock ? railSize?.width : 0,
  );

  function handleEndContentResize({ width }: ResizableLayoutSize) {
    if (width !== 'auto') {
      dock?.onResize?.(width);
    }
  }

  function selectItem(key: string) {
    if (items.find((item) => item.key === key)?.disabled) {
      return;
    }
    dock?.onSelect(key);
  }

  const toolbarItems: ToolbarItem[] = railItems.map((item) => {
    const name = item.tooltip || item.label;
    return {
      count: item.count,
      icon: { key: item.icon!, label: name },
      itemKey: item.key,
      label: name,
      maxCount: item.maxCount,
      tooltip: item.tooltip || undefined,
    };
  });

  const endPanel = hasDesktopDock ? (
    <div
      className={clsx('eds-app-layout--end-content', {
        'eds-app-layout--end-content--closed': !activeItem,
      })}
    >
      {items
        .filter((item) => item.content != null)
        .map((item) => (
          <div
            key={item.key}
            aria-label={item.label}
            className={clsx('eds-app-layout--panel', {
              'eds-app-layout--panel--hidden': item.key !== activeItem?.key,
            })}
            hidden={item.key !== activeItem?.key}
            role="region"
            // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- overflow region must be keyboard-focusable (eds-constitution §4)
            tabIndex={0}
            {...dock?.panelDataAttributes}
          >
            {item.content}
          </div>
        ))}
    </div>
  ) : null;

  const style = {
    '--_header-height': `${headerHeight}px`,
    '--_top-nav-height': `${topNavHeight}px`,
  } as CSSProperties;

  return (
    <>
      <Layout
        ref={setLayoutEl}
        className={clsx(
          'eds-app-layout',
          `eds-app-layout--${layout}`,
          framed ? 'eds-app-layout--framed' : 'eds-app-layout--unframed',
          { 'eds-app-layout--mobile': isMobile },
        )}
        direction="column"
        spacing="none"
        style={style}
      >
        {topNav && (
          <div ref={setTopNavEl} className="eds-app-layout--top-nav">
            <AppNavBreadcrumbsProvider breadcrumbs={breadcrumbs}>
              {topNav}
            </AppNavBreadcrumbsProvider>
          </div>
        )}
        <Layout
          className="eds-app-layout--body"
          direction="row"
          flex="1"
          spacing="none"
        >
          {sideNav && !isMobile && (
            <div className="eds-app-layout--side-nav">{sideNav}</div>
          )}
          <Layout
            className="eds-app-layout--content"
            direction="column"
            flex="1"
            spacing="none"
          >
            <Layout
              as="main"
              className="eds-app-layout--card"
              direction="column"
              flex="1"
              id={mainId}
              spacing="none"
            >
              {header && (
                <Layout
                  ref={setHeaderEl}
                  className="eds-app-layout--header"
                  spacing="none"
                >
                  {header}
                </Layout>
              )}
              {secondaryHeader && (
                <Layout
                  className="eds-app-layout--secondary-header"
                  direction="column"
                  spacing="none"
                >
                  {secondaryHeader}
                </Layout>
              )}
              <Layout
                ref={handleRowRef}
                className="eds-app-layout--row"
                direction="row"
                flex="1"
                spacing="none"
              >
                <Layout
                  className="eds-app-layout--main"
                  direction="column"
                  flex="1"
                  spacing="none"
                >
                  {children}
                </Layout>
                {hasDesktopDock && (
                  <Layout
                    className="eds-app-layout--dock"
                    direction="row"
                    spacing="none"
                  >
                    {activeItem ? (
                      <ResizableLayout
                        key={dock?.resetKey ?? 'end-content'}
                        defaultWidth={resolveEndContentWidth({
                          maxWidth: maxEndContentWidth,
                          width: dock?.width,
                        })}
                        direction="left"
                        maxWidth={maxEndContentWidth ?? 'available'}
                        minWidth={END_CONTENT_MIN_WIDTH}
                        onResize={handleEndContentResize}
                      >
                        {endPanel}
                      </ResizableLayout>
                    ) : (
                      endPanel
                    )}
                    <Layout
                      ref={setRailEl}
                      className="eds-app-layout--rail"
                      direction="column"
                      spacing="none"
                      {...dock?.railDataAttributes}
                    >
                      <Toolbar
                        activeItemKey={activeItem?.key}
                        disabled={dock?.disabled}
                        items={toolbarItems}
                        variant="embedded"
                        onSelectItem={(item) => selectItem(item.itemKey)}
                      />
                    </Layout>
                  </Layout>
                )}
              </Layout>
              {hasMobileDock && dock && (
                <BottomNav
                  activeValue={activeItem?.key}
                  items={railItems.map((item) => ({
                    disabled: item.disabled,
                    icon: item.icon!,
                    label: item.label,
                    value: item.key,
                  }))}
                  label={dock.controlsLabel}
                  onSelect={selectItem}
                />
              )}
            </Layout>
          </Layout>
        </Layout>
      </Layout>
      {sideNav && isMobile && (
        <NavDrawer
          isOpen={sideNavOpen}
          root={overlayRoot}
          onClose={onSideNavClose}
        >
          {sideNav}
        </NavDrawer>
      )}
      {hasMobileDock && dock && (
        <BottomSheet
          dismissLabel={dock.dismissLabel}
          isOpen={Boolean(activeItem)}
          label={activeItem?.label}
          root={overlayRoot}
          onClose={dock.onClose}
        >
          {activeItem?.content}
        </BottomSheet>
      )}
    </>
  );
});

AppLayout.displayName = 'AppLayout';
